import { DEFAULT_WINDOWING } from "../constants/windowPresets";

/** DICOM PS3.3 C.11.2: validate a complete window, including LINEAR's width=1 threshold. */
export function getDicomWindow(
  center: number | null | undefined,
  width: number | null | undefined,
  voiLUTFunction = "LINEAR",
): { center: number; width: number } | undefined {
  if (center == null || width == null || !Number.isFinite(center) || !Number.isFinite(width)) {
    return undefined;
  }
  const validWidth =
    voiLUTFunction === "SIGMOID" || voiLUTFunction === "LINEAR_EXACT" ? width > 0 : width >= 1;
  return validWidth ? { center, width } : undefined;
}

interface VoiLutMetadata {
  windowCenter?: number | number[];
  windowWidth?: number | number[];
  voiLUTFunction?: string;
}

/**
 * Used before image/volume creation for both API and raw-DICOM metadata.
 * Invalid windows become absent, so Cornerstone derives its initial range
 * from the pixels. Returning the module still prevents a lower-priority
 * provider from reintroducing invalid windows. Preserve LUT sequences and
 * paired multi-value windows; the image loader expects arrays, even for one.
 */
export function normalizeVoiLutMetadata<T extends VoiLutMetadata>(metadata: T) {
  const centers = Array.isArray(metadata.windowCenter)
    ? metadata.windowCenter
    : [metadata.windowCenter];
  const widths = Array.isArray(metadata.windowWidth)
    ? metadata.windowWidth
    : [metadata.windowWidth];
  const windows = centers.flatMap((center, i) => {
    const window = getDicomWindow(center, widths[i], metadata.voiLUTFunction);
    return window ? [window] : [];
  });
  return {
    ...metadata,
    windowCenter: windows.length ? windows.map((window) => window.center) : undefined,
    windowWidth: windows.length ? windows.map((window) => window.width) : undefined,
  };
}

/** Series selection uses the viewer's existing soft-tissue policy when no valid window exists. */
export function getSeriesWindowing(
  metadata: { window_center?: number | null; window_width?: number | null } | undefined,
) {
  return (
    getDicomWindow(metadata?.window_center, metadata?.window_width) ?? { ...DEFAULT_WINDOWING }
  );
}
